import { supabase } from './supabase'

/** Roles possibles. La contrainte `profils_role_valide` (0001) fait foi. */
export const ROLES = ['utilisateur', 'admin'] as const
export type Role = (typeof ROLES)[number]

export interface Profil {
  readonly id: string
  readonly email: string
  readonly role: Role
  readonly actif: boolean
  readonly cree_le: string
}

export class ErreurProfil extends Error {
  override readonly cause: unknown

  constructor(message: string, cause?: unknown) {
    super(message)
    this.name = 'ErreurProfil'
    this.cause = cause
  }
}

/**
 * Profil de l'utilisateur connecte.
 *
 * Rend `null` aussi bien pour un profil absent que pour un profil desactive, et
 * ce n'est pas une approximation : la policy `profils_lecture_soi_ou_admin`
 * exige `app.est_actif()`, donc un compte revoque ne lit meme plus sa propre
 * ligne. L'absence EST le signal — l'appelant traite les deux cas de la meme
 * facon, ce qui est exactement ce qu'on veut.
 */
export async function lireMonProfil(id: string): Promise<Profil | null> {
  const { data, error } = await supabase
    .from('profils')
    .select('id, email, role, actif, cree_le')
    .eq('id', id)
    .maybeSingle()

  if (error) throw new ErreurProfil(error.message, error)
  return (data as Profil | null) ?? null
}

/** Tous les profils. Reserve aux admins par la RLS, pas par cette fonction. */
export async function listerProfils(): Promise<Profil[]> {
  const { data, error } = await supabase
    .from('profils')
    .select('id, email, role, actif, cree_le')
    .order('email', { ascending: true })

  if (error) throw new ErreurProfil(error.message, error)
  return (data ?? []) as Profil[]
}

/**
 * Active ou desactive un compte.
 *
 * Un admin ne peut pas se desactiver lui-meme : le trigger
 * `profils_protege_admin_contre_lui_meme` (0006) le refuse en base. L'ecran ne
 * propose pas le bouton, mais c'est la base qui tranche — la meme requete part
 * depuis la console du navigateur.
 */
export async function definirActif(id: string, actif: boolean): Promise<void> {
  const { error } = await supabase.from('profils').update({ actif }).eq('id', id)
  if (error) throw new ErreurProfil(error.message, error)
}

export async function definirRole(id: string, role: Role): Promise<void> {
  const { error } = await supabase.from('profils').update({ role }).eq('id', id)
  if (error) throw new ErreurProfil(error.message, error)
}
